import { Box } from "@mui/material";
import { useTranslation } from "react-i18next";
import {
  ReportPeriodicity,
  type IReportColumn,
} from "../../../../models/report.dto";
import { ReportColumnType } from "../../../../models/report.dto";
import {
  translateReportLabel,
  type IReportTableRow,
} from "./reportExecutionUtils";
import {
  getReferenceColumnHeaderName,
  isMonthlyPeriodicity,
} from "./reportExecutionReferenceColumn";
import {
  emptySx,
  tableSx,
  tableWrapperSx,
  totalRowSx,
} from "./reportResultStyles";

interface IReportPrintableTableProps {
  columns: IReportColumn[];
  rows: IReportTableRow[];
  emptyMessage: string;
  periodicity?: ReportPeriodicity | null;
  showTotals?: boolean;
  summaryOnly?: boolean;
}

const EMPTY_VALUE_LABEL = "-";

const REPORT_COLUMN_TYPE_MAP: Record<string, ReportColumnType> = {
  text: ReportColumnType.Text,
  number: ReportColumnType.Number,
  date: ReportColumnType.Date,
  datetime: ReportColumnType.DateTime,
  currency: ReportColumnType.Currency,
  percentage: ReportColumnType.Percentage,
  boolean: ReportColumnType.Boolean,
};

export const normalizeColumnType = (type: IReportColumn["type"]) => {
  if (typeof type === "number") {
    return type;
  }

  if (typeof type !== "string") {
    return undefined;
  }

  const normalizedType = type.trim().toLocaleLowerCase();
  const numericType = Number(normalizedType);

  if (!Number.isNaN(numericType) && numericType > 0) {
    return numericType as ReportColumnType;
  }

  return REPORT_COLUMN_TYPE_MAP[normalizedType];
};

const formatDateValue = (
  value: string,
  language: string,
  includeTime: boolean,
) => {
  const parsedDate = new Date(value);

  if (Number.isNaN(parsedDate.getTime())) {
    return value;
  }

  return new Intl.DateTimeFormat(language, {
    dateStyle: "short",
    ...(includeTime ? { timeStyle: "short" } : {}),
  }).format(parsedDate);
};

const formatNumberValue = (value: number, language: string) =>
  new Intl.NumberFormat(language).format(value);

const formatCurrencyValue = (
  value: number,
  language: string,
  format?: string | null,
) => {
  const currency = format?.trim().toUpperCase() || "BRL";

  return new Intl.NumberFormat(language, {
    style: "currency",
    currency,
  }).format(value);
};

const formatCellValue = (
  value: unknown,
  column: IReportColumn,
  language: string,
  trueLabel: string,
  falseLabel: string,
) => {
  if (value === null || value === undefined || value === "") {
    return EMPTY_VALUE_LABEL;
  }

  const columnType = normalizeColumnType(column.type);

  if (typeof value === "number" && Number.isFinite(value)) {
    switch (columnType) {
      case ReportColumnType.Currency:
        return formatCurrencyValue(value, language, column.format);
      case ReportColumnType.Percentage:
        return `${formatNumberValue(value, language)}%`;
      case ReportColumnType.Number:
        return formatNumberValue(value, language);
      default:
        return String(value);
    }
  }

  if (typeof value === "boolean") {
    if (columnType === ReportColumnType.Boolean) {
      return value ? trueLabel : falseLabel;
    }

    return String(value);
  }

  if (typeof value === "string") {
    switch (columnType) {
      case ReportColumnType.Date:
        return formatDateValue(value, language, false);
      case ReportColumnType.DateTime:
        return formatDateValue(value, language, true);
      default:
        return value;
    }
  }

  return String(value);
};

const getColumnTextAlign = (column: IReportColumn) => {
  const normalizedAlign = column.align?.trim().toLocaleLowerCase();

  if (
    normalizedAlign === "left" ||
    normalizedAlign === "right" ||
    normalizedAlign === "center" ||
    normalizedAlign === "justify"
  ) {
    return normalizedAlign;
  }

  const columnType = normalizeColumnType(column.type);

  if (
    columnType === ReportColumnType.Number ||
    columnType === ReportColumnType.Currency ||
    columnType === ReportColumnType.Percentage
  ) {
    return "right";
  }

  return "left";
};

const getColumnWidthWeight = (column: IReportColumn) => {
  const columnType = normalizeColumnType(column.type);

  if (
    columnType === ReportColumnType.Number ||
    columnType === ReportColumnType.Currency ||
    columnType === ReportColumnType.Percentage ||
    columnType === ReportColumnType.Boolean
  ) {
    return 1;
  }

  if (
    columnType === ReportColumnType.Date ||
    columnType === ReportColumnType.DateTime
  ) {
    return 1.5;
  }

  return 2.5;
};

const normalizeComparableText = (value?: string | null) =>
  value?.trim().toLocaleLowerCase() ?? "";

const isReferenceColumn = (column: IReportColumn) => {
  const normalizedKey = normalizeComparableText(column.key);
  const normalizedLabel = normalizeComparableText(column.label);

  return (
    normalizedKey.includes("referencia") ||
    normalizedKey.includes("reference") ||
    normalizedLabel.endsWith(".monthreference") ||
    normalizedLabel.endsWith(".datereference") ||
    normalizedLabel.endsWith(".periodreference")
  );
};

const resolveReferenceColumn = (columns: IReportColumn[]) => {
  const explicitReferenceColumn = columns.find(isReferenceColumn);

  if (explicitReferenceColumn) {
    return explicitReferenceColumn;
  }

  return undefined;
};

const getSummaryLabelColumn = (columns: IReportColumn[]) =>
  columns.find((column) => column.isTotalizable !== true) ?? columns[0];

const getSummaryRowValue = (rows: IReportTableRow[], column: IReportColumn) => {
  const total = rows.reduce((sum, row) => {
    const rawValue = row.values[column.key];

    if (typeof rawValue === "number" && Number.isFinite(rawValue)) {
      return sum + rawValue;
    }

    if (typeof rawValue === "string") {
      const parsedValue = Number(rawValue);

      if (Number.isFinite(parsedValue)) {
        return sum + parsedValue;
      }
    }

    return sum;
  }, 0);

  return total;
};

const buildSummaryRow = (
  rows: IReportTableRow[],
  columns: IReportColumn[],
  totalLabel: string,
): IReportTableRow | null => {
  const totalizableColumns = columns.filter(
    (column) => column.isTotalizable === true,
  );

  if (!totalizableColumns.length) {
    return null;
  }

  const labelColumn = getSummaryLabelColumn(columns);

  if (!labelColumn) {
    return null;
  }

  const values: Record<string, unknown> = {};

  columns.forEach((column) => {
    if (column.key === labelColumn.key) {
      values[column.key] = totalLabel;
      return;
    }

    if (column.isTotalizable === true) {
      values[column.key] = getSummaryRowValue(rows, column);
    }
  });

  return {
    id: `${rows[0]?.id ?? "report"}-total`,
    isTotal: true,
    values,
  };
};

export type TReportTranslate = (key: string, defaultValue?: string) => string;

export const isNumericReportColumn = (column: IReportColumn) => {
  const columnType = normalizeColumnType(column.type);

  return (
    columnType === ReportColumnType.Number ||
    columnType === ReportColumnType.Currency ||
    columnType === ReportColumnType.Percentage
  );
};

interface IResolveReportTableParams {
  columns: IReportColumn[];
  rows: IReportTableRow[];
  periodicity?: ReportPeriodicity | null;
  showTotals?: boolean;
  summaryOnly?: boolean;
  t: TReportTranslate;
  language: string;
}

export const resolveReportTable = ({
  columns,
  rows,
  periodicity,
  showTotals = false,
  summaryOnly = false,
  t,
  language,
}: IResolveReportTableParams) => {
  const trueLabel = t("common.yes", "Sim");
  const falseLabel = t("common.no", "Nao");
  const totalLabel = t("page.reports.execution.table.total", "Total");
  const referenceColumn = resolveReferenceColumn(columns);
  const hideReferenceColumn =
    isMonthlyPeriodicity(periodicity) &&
    !!referenceColumn &&
    columns.length > 1;
  const visibleColumns = hideReferenceColumn
    ? columns.filter((column) => column.key !== referenceColumn?.key)
    : columns;
  const hasExplicitTotalRow = rows.some((row) => row.isTotal === true);
  const dataRows = rows.filter((row) => row.isTotal !== true);
  const summaryRow =
    showTotals && !hasExplicitTotalRow && dataRows.length > 0
      ? buildSummaryRow(dataRows, visibleColumns, totalLabel)
      : null;
  const shouldUseSummaryRows =
    showTotals &&
    !hasExplicitTotalRow &&
    dataRows.length > 0 &&
    summaryRow !== null;
  const visibleRows = summaryOnly
    ? hasExplicitTotalRow
      ? rows.filter((row) => row.isTotal === true)
      : summaryRow !== null
        ? [summaryRow]
        : []
    : shouldUseSummaryRows
      ? [...dataRows, summaryRow]
      : rows;

  const getColumnLabel = (column: IReportColumn) => {
    if (referenceColumn?.key === column.key) {
      return getReferenceColumnHeaderName(t, periodicity);
    }

    return translateReportLabel(column.label, t);
  };

  const getDisplayValue = (
    row: IReportTableRow,
    column: IReportColumn,
    columnIndex: number,
  ) => {
    const formattedValue = formatCellValue(
      row.values[column.key],
      column,
      language,
      trueLabel,
      falseLabel,
    );

    return row.isTotal &&
      hideReferenceColumn &&
      columnIndex === 0 &&
      formattedValue === EMPTY_VALUE_LABEL
      ? totalLabel
      : row.isTotal &&
          formattedValue === EMPTY_VALUE_LABEL &&
          column.isTotalizable !== true
        ? ""
        : formattedValue;
  };

  return { visibleColumns, visibleRows, getColumnLabel, getDisplayValue };
};

const ReportPrintableTable: React.FC<IReportPrintableTableProps> = ({
  columns,
  rows,
  emptyMessage,
  periodicity,
  showTotals = false,
  summaryOnly = false,
}) => {
  const { i18n, t } = useTranslation();

  if (!rows.length || !columns.length) {
    return <Box sx={emptySx}>{emptyMessage}</Box>;
  }

  const { visibleColumns, visibleRows, getColumnLabel, getDisplayValue } =
    resolveReportTable({
      columns,
      rows,
      periodicity,
      showTotals,
      summaryOnly,
      t,
      language: i18n.language,
    });
  const totalColumnWeight = visibleColumns.reduce(
    (sum, column) => sum + getColumnWidthWeight(column),
    0,
  );

  return (
    <Box sx={tableWrapperSx}>
      <Box component="table" sx={tableSx}>
        <colgroup>
          {visibleColumns.map((column) => {
            const widthWeight = getColumnWidthWeight(column);
            const widthPercentage =
              totalColumnWeight > 0
                ? `${(widthWeight / totalColumnWeight) * 100}%`
                : "auto";

            return <col key={column.key} style={{ width: widthPercentage }} />;
          })}
        </colgroup>
        <thead>
          <tr>
            {visibleColumns.map((column) => (
              <th
                key={column.key}
                scope="col"
                style={{ textAlign: getColumnTextAlign(column) }}
              >
                {getColumnLabel(column)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {visibleRows.map((row) => (
            <Box
              component="tr"
              key={row.id}
              sx={row.isTotal ? totalRowSx : undefined}
            >
              {visibleColumns.map((column, columnIndex) => (
                <td
                  key={`${row.id}-${column.key}`}
                  style={{ textAlign: getColumnTextAlign(column) }}
                >
                  {getDisplayValue(row, column, columnIndex)}
                </td>
              ))}
            </Box>
          ))}
        </tbody>
      </Box>
    </Box>
  );
};

export default ReportPrintableTable;
